
import { getScrollableParent } from '@/lib/scroll';
import KeyCode from '@/enums/KeyCode';

export default {
  name: 'AppModeless',
  props: {
    align: {
      type: String,
      default: 'left'
    },
    menuGap: {
      type: Number,
      default: 8
    },
    forceBottom: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isInDialog: false,
      hideForTransition: true,
      horizontalPlacement: this.align,
      verticalPlacement: 'bottom',
      marginLeft: null,
      maxHeight: null
    };
  },
  mounted() {
    this.hideForTransition = false;
    this.$nextTick(() => {
      const dialog = this.$el;

      const dialogWidth = dialog.offsetWidth;
      const dialogHeight = dialog.offsetHeight;

      const parent = this.getParent(dialog);
      if (!parent) return;

      const scrollParent = getScrollableParent(parent);

      const scrollRect = scrollParent.getBoundingClientRect();
      dialog.style.setProperty('display', 'none');
      const { scrollWidth, scrollHeight } = scrollParent;
      dialog.style.setProperty('display', '');

      const parentRect = parent.getBoundingClientRect();
      const parentLeft =
        scrollParent.scrollLeft + parentRect.left - scrollRect.left;
      const parentRight = parentLeft + parentRect.width;
      const parentTop =
        scrollParent.scrollTop + parentRect.top - scrollRect.top;
      const parentBottom = parentTop + parentRect.height;

      const buffer = scrollParent === document.body ? 12 : 0;

      if (this.align === 'center') {
        const minLeft = buffer;
        const maxLeft = scrollWidth - buffer - dialogWidth;

        const parentCenter = parentLeft + parentRect.width * 0.5;
        const minMarginLeft = minLeft - parentCenter;
        const maxMarginLeft = maxLeft - parentCenter;
        this.marginLeft = `${Math.min(
          Math.max(dialogWidth * -0.518, minMarginLeft),
          maxMarginLeft
        )}px`;
      } else if (this.align === 'auto') {
        const buttonHiddenLeft = parentLeft < scrollParent.scrollLeft;
        const rightPadding = 16;
        const dialogRightOverflow =
          parentLeft + dialogWidth + rightPadding > scrollRect.width;
        const hasEnoughSpaceForDialog =
          parentRect.left + dialogWidth < scrollRect.width;

        if (dialogRightOverflow) {
          if (hasEnoughSpaceForDialog) {
            this.marginLeft = `${parentRect.left}px`;
            this.horizontalPlacement = 'left';
          } else this.horizontalPlacement = 'right';
        } else if (buttonHiddenLeft) {
          this.horizontalPlacement = 'left';
        } else {
          this.horizontalPlacement = null;
          this.marginLeft = `-${scrollParent.scrollLeft}px`;
        }
      } else {
        const marginWhenPlacedLeft = scrollWidth - (parentLeft + dialogWidth);
        const marginWhenPlacedRight = parentRight - dialogWidth;

        if (marginWhenPlacedRight > 0 || marginWhenPlacedLeft > 0) {
          if (this.align === 'right') {
            if (marginWhenPlacedRight > 0) this.horizontalPlacement = 'right';
            else this.horizontalPlacement = 'left';
          } else {
            if (marginWhenPlacedLeft > 0) this.horizontalPlacement = 'left';
            else this.horizontalPlacement = 'right';
          }
        } else {
          this.horizontalPlacement =
            marginWhenPlacedLeft < marginWhenPlacedRight ? 'right' : 'left';
        }
      }

      this.setContentHeight(parentRect);

      const resolvedHeight = this.maxHeight ?? dialogHeight;
      const hasEnoughSpaceBelow =
        parentBottom + resolvedHeight <= scrollHeight - buffer;
      const hasEnoughSpaceAbove = parentTop - resolvedHeight >= buffer;

      if (hasEnoughSpaceBelow) this.verticalPlacement = 'bottom';
      else if (hasEnoughSpaceAbove) this.verticalPlacement = 'top';
      else if (this.isInDialog || this.forceBottom)
        this.verticalPlacement = 'bottom';
      else {
        this.calculateFixedPosition(this.horizontalPlacement, parentRect);
        this.verticalPlacement = 'fixed';
        this.horizontalPlacement = null;
      }
    });
    setTimeout(() => {
      document.addEventListener('click', this.click, true);
      document.addEventListener('keyup', this.keyup);
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.click, true);
    document.removeEventListener('keyup', this.keyup);
    if (this.verticalPlacement === 'fixed')
      document.getElementById('main-body').style.overflowY = '';
  },
  methods: {
    click(e) {
      const dialog = this.$el;
      const dropdown = dialog.closest('.js-dropdown-button');
      if (dropdown) {
        if (!dropdown.contains(e.target)) this.$emit('close');
        return;
      }
      if (!dialog.contains(e.target)) this.$emit('close');
    },
    keyup(e) {
      if (e.keyCode === KeyCode.ESCAPE) this.$emit('close');
    },
    calculateFixedPosition(horizontalPlacement, parentRect) {
      const dialog = this.$el;
      if (horizontalPlacement === 'right') {
        dialog.style.setProperty(
          'left',
          `${parentRect.left - dialog.offsetWidth + parentRect.width}px`
        );
      } else if (horizontalPlacement === 'left') {
        dialog.style.setProperty('left', `${parentRect.left}px`);
      } else if (horizontalPlacement === 'center') {
        this.marginLeft = 0;
        dialog.style.setProperty(
          'left',
          `${parentRect.left + parentRect.width / 2}px`
        );
      }
      dialog.style.setProperty(
        'top',
        `${parentRect.top + parentRect.height}px`
      );
      document.getElementById('main-body').style.overflowY = 'hidden';
    },
    getParent(dialog) {
      return this.align === 'auto' ? dialog.parentNode : dialog.offsetParent;
    },
    setContentHeight(parentRect) {
      const menuGap = 8;
      const minDialogHeight = 120;
      const maxDialogHeight = 480;
      const bottomSpace =
        document.body.offsetHeight - (parentRect.bottom + menuGap);

      if (bottomSpace < maxDialogHeight) {
        this.maxHeight = Math.max(bottomSpace, minDialogHeight);
      }
    }
  }
};
